import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import AdminNav from '../../components/AdminNav';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { adminApi } from '../../services/api';
import { Routes } from '../../routes';

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function inviteUrl(token) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${Routes.AdminAcceptInvite.path}?token=${encodeURIComponent(token)}`;
}

export default function AdminOperators() {
  const { username, logout, accessToken } = useAdminAuth();
  const [operators, setOperators] = useState([]);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!accessToken) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await adminApi.listOperators();
      setOperators(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load operators');
      setOperators([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInviteLink('');
    setInviting(true);
    try {
      const { data } = await adminApi.inviteOperator(inviteUsername.trim());
      setInviteLink(inviteUrl(data.inviteToken));
      setSuccess(`Invite created for ${data.username}. Copy the link below — it is shown only once.`);
      setInviteUsername('');
      await load();
    } catch (err) {
      setError(err.message || 'Invite failed');
    } finally {
      setInviting(false);
    }
  };

  const handleDisable = async (id, name) => {
    if (!window.confirm(`Disable operator “${name}”? They will no longer be able to sign in.`)) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await adminApi.disableOperator(id);
      setSuccess(`Disabled ${name}`);
      await load();
    } catch (err) {
      setError(err.message || 'Disable failed');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setSuccess('Invite link copied to clipboard.');
    } catch {
      setError('Could not copy — select the link manually.');
    }
  };

  return (
    <>
      <PageHeader
        title="Operators"
        subtitle={`Platform admin${username ? ` · ${username}` : ''}`}
      >
        <Button variant="outline-danger" size="sm" onClick={logout}>
          <FontAwesomeIcon icon={faSignOutAlt} className="me-1" />
          Sign out
        </Button>
      </PageHeader>

      <AdminNav />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      {inviteLink ? (
        <Card border="light" className="shadow-sm mb-4">
          <Card.Body>
            <h6 className="mb-2">One-time invite link</h6>
            <p className="text-gray small mb-2">Share this URL with the invitee. It expires and cannot be retrieved again.</p>
            <code className="d-block small text-break mb-3">{inviteLink}</code>
            <Button size="sm" variant="outline-primary" onClick={copyLink}>
              Copy link
            </Button>
            <Button size="sm" variant="link" className="ms-2" onClick={() => setInviteLink('')}>
              Dismiss
            </Button>
          </Card.Body>
        </Card>
      ) : null}

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <h6 className="mb-3">Invite operator</h6>
          <Form onSubmit={handleInvite}>
            <Row className="g-2 align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    required
                    maxLength={64}
                    value={inviteUsername}
                    onChange={(e) => setInviteUsername(e.target.value)}
                    placeholder="ops_name"
                  />
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button type="submit" variant="primary" disabled={inviting}>
                  {inviting ? 'Creating…' : 'Create invite'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead className="thead-light">
              <tr>
                <th>Username</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center text-gray py-4">
                    Loading…
                  </td>
                </tr>
              ) : operators.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-gray py-4">
                    No operators yet.
                  </td>
                </tr>
              ) : (
                operators.map((op) => (
                  <tr key={op.id}>
                    <td className="fw-bold">{op.username}</td>
                    <td>
                      <span
                        className={`badge bg-${
                          op.status === 'ACTIVE'
                            ? 'success'
                            : op.status === 'INVITED'
                              ? 'warning'
                              : 'secondary'
                        }`}
                      >
                        {op.status}
                      </span>
                    </td>
                    <td className="text-gray small">{formatTs(op.createdAt)}</td>
                    <td className="text-end">
                      {op.status !== 'DISABLED' ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => handleDisable(op.id, op.username)}
                        >
                          Disable
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
}
